import { useState } from 'react';
import { Alert, Image, Pressable, ScrollView, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { createTask, uploadTaskPhoto, TaskType } from '../api/tasks';
import { Button, Checkbox, ErrorText, FormInput, Label } from '../components/ui';
import { CameraCapture } from '../components/CameraCapture';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';

const ADVANCE_PAYMENT_MIN_TIERS = ['DIAMOND', 'LEGEND'];
const ITEM_PHOTO_TASK_TYPES: TaskType[] = ['HOME_MOVING', 'DELIVERY'];

const TASK_TYPE_OPTIONS: {
  value: TaskType;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  hint: string;
  defaultRequiresPurchase: boolean;
  audience: 'business' | 'individual' | 'both';
}[] = [
  {
    value: 'HOUSEHOLD_HELP',
    label: 'Tìm người giúp việc theo giờ',
    icon: 'home-outline',
    hint: 'Dọn dẹp, nấu ăn hoặc chăm sóc theo giờ tại địa chỉ của bạn. Vui lòng ghi rõ số giờ cần và chi tiết công việc (diện tích cần dọn, các đầu việc cụ thể) để người nhận việc ước lượng đúng công sức.',
    defaultRequiresPurchase: false,
    audience: 'individual',
  },
  {
    value: 'HOME_MOVING',
    label: 'Tìm người chuyển nhà',
    icon: 'cube-outline',
    hint: 'Khuân vác và vận chuyển đồ đạc từ địa chỉ này sang địa chỉ khác. Vui lòng đính kèm ảnh chụp đồ cần chuyển và địa chỉ cần đến để người nhận việc chuẩn bị đúng phương tiện.',
    defaultRequiresPurchase: false,
    audience: 'individual',
  },
  {
    value: 'PERSONAL_SHOPPING',
    label: 'Nhờ mua đồ hộ',
    icon: 'bag-handle-outline',
    hint: 'Người nhận việc tự ứng tiền mua đồ trước, bạn hoàn lại riêng ngoài số xu thưởng. Chỉ chủ shop hạng Kim cương hoặc Huyền thoại mới được yêu cầu ứng tiền, và cần khai báo rõ số tiền tối đa để bảo vệ người nhận việc.',
    defaultRequiresPurchase: false,
    audience: 'individual',
  },
  {
    value: 'DELIVERY',
    label: 'Nhờ giao hàng',
    icon: 'bicycle-outline',
    hint: 'Nhờ lấy và giao đồ giữa hai địa điểm trong khu vực. Vui lòng đính kèm ảnh chụp món đồ cần giao và địa chỉ cần đến để người nhận việc dễ nhận diện.',
    defaultRequiresPurchase: false,
    audience: 'individual',
  },
  {
    value: 'CUSTOMER_REFERRAL',
    label: 'Giới thiệu khách hàng',
    icon: 'people-outline',
    hint: 'Người nhận việc giới thiệu khách thật đến shop, không cần có mặt tại shop — chỉ cần gửi tên và số điện thoại khách để bạn đối chiếu trước khi duyệt. Có hai cách trả thưởng: mức xu cố định cao hơn một chút (vì có khách là có doanh thu), hoặc phần trăm trên giá trị đơn hàng khách đó thực hiện. Nếu chọn trả theo phần trăm, nên yêu cầu người giới thiệu có mặt trực tiếp lúc khách mua hàng để tránh tranh chấp — Loot không xử lý tranh chấp phát sinh giữa hai bên.',
    defaultRequiresPurchase: false,
    audience: 'business',
  },
  {
    value: 'MYSTERY_SHOPPER',
    label: 'Khách hàng bí ẩn',
    icon: 'glasses-outline',
    hint: 'Người nhận việc ghé shop, trải nghiệm dịch vụ như khách bình thường rồi gửi nhận xét riêng cho bạn theo đúng tiêu chí bạn đề ra (thái độ phục vụ, chất lượng, thời gian chờ...) — không đăng công khai. Vì đòi hỏi thời gian và sự khách quan, nên trả thưởng cao hơn, khoảng 100–200 xu.',
    defaultRequiresPurchase: true,
    audience: 'business',
  },
  {
    value: 'GOOGLE_REVIEW_WITH_PURCHASE',
    label: 'Đánh giá Google (có sử dụng dịch vụ)',
    icon: 'cart-outline',
    hint: 'Người nhận việc đến sử dụng dịch vụ thật rồi đánh giá trên Google Maps — hình thức đáng tin cậy nhất, nên trả thưởng cao hơn vì người thực hiện có phát sinh chi phí thật.',
    defaultRequiresPurchase: true,
    audience: 'business',
  },
  {
    value: 'GOOGLE_REVIEW_NO_PURCHASE',
    label: 'Đánh giá Google (không cần sử dụng dịch vụ)',
    icon: 'star-outline',
    hint: 'Ghé shop trực tiếp, chụp ảnh và để lại đánh giá trên Google Maps — không bắt buộc sử dụng dịch vụ.',
    defaultRequiresPurchase: false,
    audience: 'business',
  },
  {
    value: 'SOCIAL_CHECKIN',
    label: 'Check-in và đăng Facebook/Instagram',
    icon: 'camera-outline',
    hint: 'Không cần đánh giá trên Google — chỉ cần chụp ảnh, check-in và đăng lên mạng xã hội cá nhân. Mức thưởng nên tương đương với hình thức đánh giá Google không cần sử dụng dịch vụ.',
    defaultRequiresPurchase: false,
    audience: 'business',
  },
  {
    value: 'CUSTOM',
    label: 'Nhiệm vụ tự đề xuất',
    icon: 'create-outline',
    hint: 'Tự mô tả nhiệm vụ theo nhu cầu riêng. Mất phí 10 xu không hoàn lại khi đề xuất, và cần admin duyệt trước khi hiển thị cho người nhận việc.',
    defaultRequiresPurchase: true,
    audience: 'both',
  },
];

export default function CreateTaskScreen() {
  const { shopId } = useLocalSearchParams<{ shopId: string }>();
  const { token, user, refreshUser } = useAuth();
  const router = useRouter();
  const FREE_TASKS_FOR_NEW_OWNERS = 3;
  const tasksCreatedCount = user?.tasksCreatedCount ?? 0;
  const isFreePromoTask = tasksCreatedCount < FREE_TASKS_FOR_NEW_OWNERS;
  const feePercent = isFreePromoTask ? 0 : user?.ownerEscrowFeePercent ?? 0;
  // Which task category to show — defaults to the account's posterKind, but
  // is a per-task choice, not locked to the account: one email/shop owner
  // may want to post both personal errands and business tasks (e.g. a shop
  // owner who also needs help moving house), so let them switch freely here
  // instead of forcing two separate accounts.
  const [isIndividual, setIsIndividual] = useState(user?.posterKind === 'INDIVIDUAL');
  const visibleOptions = TASK_TYPE_OPTIONS.filter(
    (opt) => opt.audience === 'both' || opt.audience === (isIndividual ? 'individual' : 'business'),
  );

  const [taskType, setTaskType] = useState<TaskType>(
    isIndividual ? 'HOUSEHOLD_HELP' : 'GOOGLE_REVIEW_WITH_PURCHASE',
  );
  const [requiresPurchase, setRequiresPurchase] = useState(!isIndividual);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [rewardCoins, setRewardCoins] = useState('20');
  const [totalSlots, setTotalSlots] = useState('10');
  // Default kept tight — a large check-in radius makes it easy to fake being
  // physically present at the shop. Owners can still widen it if needed.
  const [checkinRadius, setCheckinRadius] = useState('50');
  const [requiredPhotos, setRequiredPhotos] = useState('3');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usePercentTerms, setUsePercentTerms] = useState(false);
  const [percentTerms, setPercentTerms] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('2');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [itemPhotoUri, setItemPhotoUri] = useState<string | null>(null);
  const [showItemCamera, setShowItemCamera] = useState(false);
  const [advanceAmountVnd, setAdvanceAmountVnd] = useState('');

  const selectedType = TASK_TYPE_OPTIONS.find((t) => t.value === taskType)!;
  const isReferral = taskType === 'CUSTOMER_REFERRAL';
  const isHouseholdHelp = taskType === 'HOUSEHOLD_HELP';
  const needsItemPhoto = ITEM_PHOTO_TASK_TYPES.includes(taskType);
  const isPersonalShopping = taskType === 'PERSONAL_SHOPPING';
  const percentTermsEligible =
    (!!user?.ownerTier && ['GOLD', 'PLATINUM', 'DIAMOND', 'LEGEND'].includes(user.ownerTier)) || !!user?.vipActive;
  const advancePaymentEligible = !!user?.ownerTier && ADVANCE_PAYMENT_MIN_TIERS.includes(user.ownerTier);
  const onSelectType = (value: TaskType) => {
    setTaskType(value);
    setRequiresPurchase(TASK_TYPE_OPTIONS.find((t) => t.value === value)!.defaultRequiresPurchase);
    if (value === 'CUSTOMER_REFERRAL') setRequiredPhotos('0');
    if (value === 'MYSTERY_SHOPPER') setRewardCoins('150');
    setItemPhotoUri(null);
    setDestinationAddress('');
    setAdvanceAmountVnd('');
  };

  const onToggleAudience = (individual: boolean) => {
    if (individual === isIndividual) return;
    setIsIndividual(individual);
    onSelectType(individual ? 'HOUSEHOLD_HELP' : 'GOOGLE_REVIEW_WITH_PURCHASE');
  };

  const rewardPerSlot = Number(rewardCoins) || 0;
  const slots = Number(totalSlots) || 0;
  const feePerSlot = Math.floor((rewardPerSlot * feePercent) / 100);
  const escrowPerSlot = rewardPerSlot + feePerSlot;
  const totalCost = escrowPerSlot * slots;

  const titlePlaceholder = isIndividual ? 'Cần người dọn nhà 2 tiếng' : 'Đánh giá trải nghiệm tại shop';
  const descriptionPlaceholder = isIndividual
    ? 'Mô tả công việc cần làm và địa điểm'
    : 'Ghé shop, trải nghiệm và đánh giá trung thực';

  const onSubmit = async () => {
    if (!token || !shopId) return;
    setError(null);
    // Mirrors CreateTaskDto's MinLength — the placeholder fallback below
    // only kicks in when a field is completely empty, so a too-short but
    // non-empty value would otherwise only get rejected after a full round
    // trip to the server.
    if (title.trim() && title.trim().length < 3) {
      setError('Tiêu đề phải có ít nhất 3 ký tự (hoặc để trống để dùng gợi ý)');
      return;
    }
    if (description.trim() && description.trim().length < 10) {
      setError('Mô tả phải có ít nhất 10 ký tự (hoặc để trống để dùng gợi ý)');
      return;
    }
    if (!instructions.trim() || instructions.trim().length < 10) {
      setError('Vui lòng điền hướng dẫn cho người nhận việc, ít nhất 10 ký tự');
      return;
    }
    const reward = Number(rewardCoins);
    if (!Number.isInteger(reward) || reward < 1 || reward > 1_000_000) {
      setError('Thưởng mỗi lượt phải là số nguyên từ 1 đến 1.000.000 xu');
      return;
    }
    const slotsCount = Number(totalSlots);
    if (!Number.isInteger(slotsCount) || slotsCount < 1 || slotsCount > 1000) {
      setError('Số lượt tối đa phải là số nguyên từ 1 đến 1000');
      return;
    }
    if (!isReferral) {
      const radius = Number(checkinRadius);
      if (!Number.isInteger(radius) || radius < 20 || radius > 2000) {
        setError('Bán kính check-in phải là số nguyên từ 20 đến 2000 mét');
        return;
      }
    }
    const photos = Number(requiredPhotos);
    if (!Number.isInteger(photos) || photos < 0 || photos > 10) {
      setError('Số ảnh bắt buộc phải là số nguyên từ 0 đến 10');
      return;
    }
    if (isReferral && percentTermsEligible && usePercentTerms && percentTerms.trim().length < 5) {
      setError('Vui lòng nhập rõ nội dung thoả thuận % đơn hàng, tối thiểu 5 ký tự');
      return;
    }
    if (isHouseholdHelp) {
      const hours = Number(estimatedHours);
      // Matches backend's @IsInt() @Max(24) on CreateTaskDto.estimatedHours —
      // without this, a value like "30" or "2.5" passed this screen's old
      // >0-only check and only failed after a full round trip to the
      // server, with the raw property name leaking into the error message.
      if (!Number.isInteger(hours) || hours < 1 || hours > 24) {
        setError('Số giờ cần phải là số nguyên từ 1 đến 24');
        return;
      }
    }
    if (needsItemPhoto && destinationAddress.trim().length < 5) {
      setError('Vui lòng nhập địa chỉ cần chuyển đến (ít nhất 5 ký tự)');
      return;
    }
    if (needsItemPhoto && !itemPhotoUri) {
      setError('Vui lòng chụp ảnh đồ cần chuyển để người nhận việc dễ nhận diện');
      return;
    }
    if (isPersonalShopping && requiresPurchase) {
      const advance = Number(advanceAmountVnd);
      // Matches backend's @IsInt() @Min(1_000) @Max(50_000_000) on advanceAmountVnd.
      if (!Number.isInteger(advance) || advance < 1_000 || advance > 50_000_000) {
        setError('Số tiền ứng trước phải là số nguyên từ 1.000đ đến 50.000.000đ');
        return;
      }
    }
    setSaving(true);
    let createdTaskId: string | undefined;
    try {
      const task = await createTask(token, {
        shopId,
        title: title.trim() || titlePlaceholder,
        description: description.trim() || descriptionPlaceholder,
        instructions,
        rewardCoins: Number(rewardCoins),
        totalSlots: Number(totalSlots),
        checkinRadiusMeters: Number(checkinRadius),
        // Referral tasks skip check-in entirely and have no photo-capture
        // step in the submission flow — force 0 regardless of the field's
        // current value so a stray edit can't force a reviewer through a
        // pointless photo step before the referral card ever appears.
        requiredPhotos: isReferral ? 0 : Number(requiredPhotos),
        taskType,
        requiresPurchase,
        referralPercentTerms: isReferral && percentTermsEligible && usePercentTerms ? percentTerms : undefined,
        estimatedHours: isHouseholdHelp ? Number(estimatedHours) : undefined,
        destinationAddress: needsItemPhoto ? destinationAddress.trim() : undefined,
        advanceAmountVnd: isPersonalShopping && requiresPurchase ? Number(advanceAmountVnd) : undefined,
      });
      // From here on, escrow is already deducted and the task row exists —
      // any failure below must NOT leave the user back on this same form
      // inviting a resubmit (which would create a second task and deduct
      // escrow a second time for what looks like one attempt).
      createdTaskId = task.id;
      if (needsItemPhoto && itemPhotoUri) {
        await uploadTaskPhoto(token, task.id, itemPhotoUri);
      }
      await refreshUser();
      router.replace(`/shop/${shopId}`);
    } catch (e) {
      if (createdTaskId) {
        // The task itself was created fine — only the photo attach failed.
        // Navigate away instead of leaving the create form up, so there's
        // nothing left to "resubmit" into a duplicate task.
        await refreshUser();
        Alert.alert(
          'Đã tạo nhiệm vụ, nhưng lỗi tải ảnh',
          'Nhiệm vụ đã được tạo thành công, nhưng ảnh đồ vật chưa tải lên được. Vào chi tiết nhiệm vụ để thử tải ảnh lại.',
        );
        router.replace(`/shop/${shopId}`);
      } else {
        setError(e instanceof ApiError ? e.message : 'Tạo nhiệm vụ thất bại (kiểm tra số dư ví)');
      }
    } finally {
      setSaving(false);
    }
  };

  if (showItemCamera) {
    return (
      <CameraCapture
        onCapture={(uri) => {
          setItemPhotoUri(uri);
          setShowItemCamera(false);
        }}
        onCancel={() => setShowItemCamera(false)}
      />
    );
  }

  return (
    <ScrollView contentContainerStyle={{ padding: 16 }}>
      {/* While a request is in flight, lock every field/toggle so the user
       * can't change the task's shape mid-submit — otherwise the live
       * escrow-preview and visible options keep updating for a request that
       * already went out describing the *previous* selection. */}
      <View pointerEvents={saving ? 'none' : 'auto'} style={{ opacity: saving ? 0.5 : 1 }}>
      <Label>Đăng nhiệm vụ cho</Label>
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
        <Pressable
          onPress={() => onToggleAudience(true)}
          style={{
            flex: 1,
            paddingVertical: 10,
            borderRadius: 10,
            borderWidth: 1.5,
            alignItems: 'center',
            borderColor: isIndividual ? colors.primary : colors.border,
            backgroundColor: isIndividual ? colors.primarySoft : colors.card,
          }}
        >
          <Text style={{ fontFamily: fonts.semiBold, color: isIndividual ? colors.primaryDark : colors.text }}>
            Cá nhân
          </Text>
        </Pressable>
        <Pressable
          onPress={() => onToggleAudience(false)}
          style={{
            flex: 1,
            paddingVertical: 10,
            borderRadius: 10,
            borderWidth: 1.5,
            alignItems: 'center',
            borderColor: !isIndividual ? colors.primary : colors.border,
            backgroundColor: !isIndividual ? colors.primarySoft : colors.card,
          }}
        >
          <Text style={{ fontFamily: fonts.semiBold, color: !isIndividual ? colors.primaryDark : colors.text }}>
            Doanh nghiệp
          </Text>
        </Pressable>
      </View>
      <Text style={{ color: colors.textMuted, fontSize: 12, marginTop: -10, marginBottom: 12 }}>
        Cùng một tài khoản có thể đăng cả nhiệm vụ cá nhân lẫn nhiệm vụ cho shop — chọn đúng loại cho
        nhiệm vụ này.
      </Text>

      <Label>Loại nhiệm vụ</Label>
      <View style={{ gap: 8, marginBottom: 8 }}>
        {visibleOptions.map((opt) => {
          const selected = taskType === opt.value;
          return (
            <Pressable
              key={opt.value}
              onPress={() => onSelectType(opt.value)}
              style={{
                padding: 12,
                borderRadius: 10,
                borderWidth: 1.5,
                borderColor: selected ? colors.primary : colors.border,
                backgroundColor: selected ? colors.primarySoft : colors.card,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Ionicons
                  name={opt.icon}
                  size={18}
                  color={selected ? colors.primary : colors.textMuted}
                />
                <Text style={{ fontFamily: fonts.bold, color: colors.text, flex: 1 }}>{opt.label}</Text>
              </View>
              <Text
                style={{ color: colors.textMuted, fontSize: 12, marginTop: 2 }}
                numberOfLines={selected ? undefined : 2}
              >
                {opt.hint}
              </Text>
            </Pressable>
          );
        })}
      </View>

      {!isReferral && !(isPersonalShopping && !advancePaymentEligible) ? (
        <Checkbox
          checked={requiresPurchase}
          onToggle={() => setRequiresPurchase((v) => !v)}
          label={
            isIndividual
              ? 'Người nhận việc cần ứng tiền mua đồ trước — bạn hoàn lại riêng, ngoài số xu thưởng'
              : 'Yêu cầu người nhận việc mua hoặc sử dụng dịch vụ tại shop — nên đặt mức thưởng đủ bù chi phí này'
          }
        />
      ) : null}

      {isPersonalShopping && !advancePaymentEligible ? (
        <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 12 }}>
          🔒 Yêu cầu người nhận việc ứng tiền mua đồ chỉ mở cho hạng Kim cương hoặc Huyền thoại — nạp
          thêm để lên hạng, hoặc thoả thuận thanh toán trước với người nhận việc ngoài ứng dụng.
        </Text>
      ) : null}

      {isPersonalShopping && advancePaymentEligible && requiresPurchase ? (
        <>
          <Label>Số tiền tối đa cần ứng (VNĐ)</Label>
          <FormInput
            value={advanceAmountVnd}
            onChangeText={setAdvanceAmountVnd}
            keyboardType="numeric"
            placeholder="Ví dụ: 300000"
          />
          <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 12 }}>
            Người nhận việc sẽ thấy rõ số tiền tối đa cần ứng trước khi nhận nhiệm vụ. Bạn hoàn lại số
            tiền này trực tiếp cho người nhận việc, ngoài ứng dụng — Loot không giữ và không xử lý
            tranh chấp phát sinh từ khoản ứng tiền này.
          </Text>
        </>
      ) : null}

      {isHouseholdHelp ? (
        <>
          <Label>Số giờ cần</Label>
          <FormInput value={estimatedHours} onChangeText={setEstimatedHours} keyboardType="numeric" />
        </>
      ) : null}

      {needsItemPhoto ? (
        <>
          <Label>Địa chỉ cần chuyển đến</Label>
          <FormInput
            value={destinationAddress}
            onChangeText={setDestinationAddress}
            placeholder="123 Nguyễn Huệ, Q1, TP.HCM"
          />
          <Label>Ảnh chụp đồ cần chuyển</Label>
          {itemPhotoUri ? (
            <Image source={{ uri: itemPhotoUri }} style={{ width: 96, height: 96, borderRadius: 8, marginBottom: 8 }} />
          ) : null}
          <Button
            title={itemPhotoUri ? 'Chụp lại ảnh' : 'Chụp ảnh đồ cần chuyển'}
            variant="outline"
            onPress={() => setShowItemCamera(true)}
          />
          <View style={{ marginBottom: 12 }} />
        </>
      ) : null}

      {taskType === 'CUSTOM' ? (
        <Text style={{ color: colors.warning, fontSize: 13, marginBottom: 12 }}>
          ⚠ Nhiệm vụ tự đề xuất mất phí 10 xu không hoàn lại khi đề xuất, và cần admin duyệt trước khi
          hiển thị cho người nhận việc.
        </Text>
      ) : null}

      {isReferral ? (
        percentTermsEligible ? (
          <>
            <Checkbox
              checked={usePercentTerms}
              onToggle={() => setUsePercentTerms((v) => !v)}
              label="Thêm thoả thuận % đơn hàng — thoả thuận riêng với người nhận việc, ngoài số xu thưởng cố định bên dưới"
            />
            {usePercentTerms ? (
              <>
                <Label>Nội dung thoả thuận % đơn hàng</Label>
                <FormInput
                  value={percentTerms}
                  onChangeText={setPercentTerms}
                  placeholder="Ví dụ: 10% giá trị đơn hàng, thanh toán trực tiếp khi khách thanh toán"
                  multiline
                />
                <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 12 }}>
                  Loot chỉ hiển thị nội dung này cho người nhận việc, không tính, không giữ và không xử lý
                  tranh chấp phát sinh từ thoả thuận riêng này.
                </Text>
              </>
            ) : null}
          </>
        ) : (
          <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 12 }}>
            🔒 Thưởng theo phần trăm đơn hàng chỉ mở cho hạng Vàng trở lên hoặc VIP — nạp thêm để lên hạng.
          </Text>
        )
      ) : null}

      <Label>Tiêu đề nhiệm vụ (để trống sẽ tự dùng gợi ý)</Label>
      <FormInput value={title} onChangeText={setTitle} placeholder={titlePlaceholder} />

      <Label>Mô tả (để trống sẽ tự dùng gợi ý)</Label>
      <FormInput
        value={description}
        onChangeText={setDescription}
        placeholder={descriptionPlaceholder}
        multiline
      />

      <Label>Hướng dẫn chi tiết cho người nhận việc</Label>
      <FormInput
        value={instructions}
        onChangeText={setInstructions}
        placeholder={
          isIndividual
            ? 'Ví dụ: địa chỉ cụ thể, thời gian mong muốn, ai chuẩn bị dụng cụ hoặc chi phí phát sinh'
            : 'Ví dụ: chụp 3 ảnh (mặt tiền, không gian, đồ uống), check-in tại shop, đánh giá thật trên Google Maps'
        }
        multiline
      />

      <Label>Thưởng mỗi lượt (xu)</Label>
      <FormInput value={rewardCoins} onChangeText={setRewardCoins} keyboardType="numeric" />

      <Label>Số lượt tối đa</Label>
      <FormInput value={totalSlots} onChangeText={setTotalSlots} keyboardType="numeric" />

      {!isReferral ? (
        <>
          <Label>Bán kính check-in (mét)</Label>
          <FormInput value={checkinRadius} onChangeText={setCheckinRadius} keyboardType="numeric" />
        </>
      ) : null}

      {!isReferral ? (
        <>
          <Label>Số ảnh bắt buộc</Label>
          <FormInput value={requiredPhotos} onChangeText={setRequiredPhotos} keyboardType="numeric" />
        </>
      ) : null}

      <Text style={{ color: colors.textMuted, marginBottom: 12 }}>
        {isFreePromoTask ? (
          <>
            Miễn phí dịch vụ — đây là nhiệm vụ thứ {tasksCreatedCount + 1}/{FREE_TASKS_FOR_NEW_OWNERS} được miễn phí cho
            chủ shop mới. Mỗi lượt: {rewardPerSlot} xu thưởng.{'\n'}
          </>
        ) : (
          <>
            Mỗi lượt: {rewardPerSlot} xu thưởng + {feePerSlot} xu phí dịch vụ ({feePercent}%) = {escrowPerSlot} xu.{'\n'}
          </>
        )}
        Sẽ giữ (escrow) tổng {totalCost || 0} xu từ ví của bạn cho đến khi nhiệm vụ hoàn tất hoặc huỷ.
      </Text>
      </View>

      <ErrorText>{error}</ErrorText>

      <Button title="Tạo nhiệm vụ" onPress={onSubmit} loading={saving} />
    </ScrollView>
  );
}
